import { Box, Button, Code, Flex, Link, ListItem, Text, UnorderedList } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { useNavigate } from "react-router";

import { AddIcon, ColumnDefinition, RanaTable } from "@nens/ranalib";
import { useTableData } from "@nens/react/hooks";
import * as api from "@nens/threedi/openapi-client";

import PersonalApiKeysSvg from "@/assets/personal_api_keys.svg?url";

import { getPersonalApiKeyApi } from "../../apiConfiguration";
import { APP_BASE_HREF } from "../../App";
import ButtonWithConfirmation from "../../components/ButtonWithConfirmation";
import { ExplainSideColumn } from "../../components/ExplainSideColumn";
import { OrganisationContext } from "../../providers/OrganisationProvider";

interface PersonalApiKeyRow extends api.PersonalAPIKey {
  uuid: string;
}

const personalApiKeyApi = getPersonalApiKeyApi();

const PersonalApiKeysList = () => {
  const navigationUrl = `${APP_BASE_HREF}/personal_api_keys`;
  const navigate = useNavigate();
  const { organisation } = useContext(OrganisationContext);
  const { limit, offset, setPageSize, setPage, page, orderingString, searchField, searchString } =
    useTableData({
      initialSearchField: "name",
      initialOrderings: { created: "za" },
      initialPageSize: 20,
    });

  const queryClient = useQueryClient();
  const personalApiKeys = useQuery({
    queryKey: [
      "personalApiKeys",
      organisation.uniqueId,
      limit,
      offset,
      searchString,
      searchString ? searchField : null,
      orderingString,
    ],
    queryFn: () =>
      personalApiKeyApi.personalapikeysList({
        limit,
        offset,
      }),
    refetchOnMount: "always",
  });

  const rows: PersonalApiKeyRow[] =
    personalApiKeys.status === "success"
      ? personalApiKeys.data.results.map((row) => {
          return { ...row, uuid: "" /*+ row.uuid*/ };
        })
      : [];

  const columnDefinitions: ColumnDefinition[] = [
    {
      title: "Name",
      width: "1fr",
      orderingField: "name",
    },
    {
      title: "Scope",
      width: "1fr",
      orderingField: "scope",
    },
    {
      title: "Created on",
      width: "1fr",
      orderingField: "created",
    },
    {
      title: "",
      width: "150px",
    },
  ];

  const renderActionCell = (row: PersonalApiKeyRow) => {
    if (row.prefix && row.revoked === false) {
      return (
        <ButtonWithConfirmation
          key={row && row.prefix}
          buttonText="Revoke"
          dialogTitle="Are you sure ?"
          confirmationButtonText="Revoke"
          confirmationButtonSafety="neutral"
          height="auto"
          onClick={async () => {
            await personalApiKeyApi.personalapikeysRevoke({
              prefix: (row && row.prefix) || "should never happen",
            });
            queryClient.invalidateQueries({ queryKey: ["personalApiKeys"] });
          }}
          disableAfterClick
        >
          <p>
            You are about to revoke the following API key:
            <br />
            <br />
            name: {row ? row.name : ""}
            <br />
            <br />
            prefix: {row ? row.prefix : ""}
            <br />
            <br />
            This can NOT be undone !
          </p>
        </ButtonWithConfirmation>
      );
    }

    if (row.revoked === true) {
      return (
        <Text
          title="Already revoked. This API key can no longer be used. Revoking cannot be undone"
          color="semantic.error.main"
          fontStyle="italic"
          ml="16px"
          mt="16px"
        >
          Revoked
        </Text>
      );
    }

    return (
      <Text ml="16px" mt="16px" title="cannot revoke, becaus api key record has no prefix">
        Cannot revoke
      </Text>
    );
  };

  return (
    <ExplainSideColumn
      imgUrl={PersonalApiKeysSvg}
      imgAltDescription={"Personal-API-key icon"}
      headerText={"Personal Api Keys"}
      explanationText={
        <>
          <Text>
            Personal API keys are used to authenticate external applications with{" "}
            <Link
              isExternal
              color="brand.analytic"
              href="https://en.wikipedia.org/wiki/Basic_access_authentication"
            >
              basic authentication
            </Link>{" "}
            as follows:
          </Text>
          <UnorderedList>
            <ListItem>
              Username is literally __key__ (with double underscores on bothsides of the word "key")
            </ListItem>
            <ListItem>Password is {"{your api key}"}</ListItem>
          </UnorderedList>
          <Text>For example:</Text>
          <Box my={2}>
            <Code>username: __key__</Code>
            <Code>password: example.apikey</Code>
          </Box>
          <Text>
            The API key itself is only visible on creation. It can not be looked up after.
          </Text>
        </>
      }
      backUrl={"/"}
    >
      <Flex direction="row" justifyContent={"flex-end"} mt={2}>
        <Button
          variant="primary"
          leftIcon={<AddIcon boxSize={5} />}
          mb={5}
          onClick={() => navigate(`${navigationUrl}/new`)}
        >
          New key
        </Button>
      </Flex>
      <RanaTable
        columnDefinitions={columnDefinitions}
        tableDataIds={rows.map((row) => row.uuid)}
        isLoading={personalApiKeys.status !== "success"}
        emptyElement={<RanaTable.Empty />}
        sx={{
          // vertical alignment for the icons and checkboxes
          "& p": {
            marginBottom: 0,
          },
          "& label": {
            marginBottom: 0,
          },
        }}
      >
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {rows.map((row, index) => (
              <RanaTable.Row key={`${row.uuid}${index}`} rowId={row.uuid}>
                <RanaTable.Cell>
                  <Text
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    title={row.name}
                  >
                    {row.name}
                  </Text>
                </RanaTable.Cell>
                <RanaTable.Cell>
                  <Text
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    title={row.scope}
                  >
                    {row.scope}
                  </Text>
                </RanaTable.Cell>
                <RanaTable.Cell>
                  <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {row.created ? row.created.toDateString() : ""}
                  </Text>
                </RanaTable.Cell>
                <RanaTable.Cell>{renderActionCell(row)}</RanaTable.Cell>
              </RanaTable.Row>
            ))}
          </RanaTable.Body>
        </RanaTable.Container>
        <RanaTable.Pagination
          page={page}
          numPages={Math.ceil((personalApiKeys.data?.count ?? 0) / limit)}
          limit={limit}
          setPageSize={setPageSize}
          setPage={setPage}
        />
      </RanaTable>
    </ExplainSideColumn>
  );
};

export default PersonalApiKeysList;
